import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  ResponsiveDialog, ResponsiveDialogContent, ResponsiveDialogHeader,
  ResponsiveDialogTitle, ResponsiveDialogFooter, ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import CategoryPicker from "@/components/CategoryPicker";
import TransferLinkPanel from "@/components/TransferLinkPanel";
import { sortCategoriesByUsage } from "@/lib/categoryOrder";

const FREQS = ["weekly", "monthly", "quarterly", "yearly"];

const TYPES = [
  { value: "expense", label: "Expense", active: "bg-expense text-white" },
  { value: "income", label: "Income", active: "bg-income text-black" },
  { value: "transfer", label: "Transfer", active: "bg-forecast text-black" },
];

const emptyForm = (accountId) => ({
  date: new Date().toISOString().slice(0, 10),
  amount: "",
  type: "expense",
  category: "",
  description: "",
  recurring: false,
  recurrence_frequency: "monthly",
  account_id: accountId,
  from_account_id: accountId,
  to_account_id: "",
});

// A transfer side knows its own account and the other side's; which is the
// source depends on whether this side is the outgoing expense.
const transferEnds = (transaction) => (
  transaction.type === "expense"
    ? [transaction.account_id, transaction.transfer_account_id]
    : [transaction.transfer_account_id, transaction.account_id]
);

function AccountSelect({ label, value, accounts, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1 bg-background" data-testid={testid}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent className="bg-card border-border">
          {accounts.map((account) => (
            <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function TransactionDialog({
  open, onOpenChange, categories, editing, onSave, onSaveTransfer,
  accounts = [], accountsById = {}, defaultAccountId,
  onFindTransferCandidates, onLinkTransfer, onUnlinkTransfer,
}) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const amountRef = useRef(null);

  /**
   * Amount is the one field every transaction needs and the only one with no
   * usable default, so it takes focus when the form opens. Radix would
   * otherwise focus the first tabbable element, which is the expense/income
   * toggle. Existing text is selected so editing an amount is a retype rather
   * than a cursor hunt.
   */
  const focusAmount = (event) => {
    event.preventDefault();
    // One frame later: the form is still animating in, and a focus set
    // mid-transition gets dropped on the way.
    requestAnimationFrame(() => {
      amountRef.current?.focus();
      amountRef.current?.select();
    });
  };

  useEffect(() => {
    if (open) {
      if (editing) {
        const [from, to] = editing.transfer_id ? transferEnds(editing) : [editing.account_id, ""];
        setForm({
          date: editing.date,
          amount: String(editing.amount),
          type: editing.transfer_id ? "transfer" : editing.type,
          category: editing.category,
          description: editing.description || "",
          recurring: !!editing.recurring,
          recurrence_frequency: editing.recurrence_frequency || "monthly",
          account_id: editing.account_id,
          from_account_id: from,
          to_account_id: to,
        });
      } else {
        setForm(emptyForm(defaultAccountId));
      }
    }
    // The default account only seeds a new form; a scope change while the
    // form is open must not overwrite the user's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  // Opened straight from a URL, the form can beat the account list here; fill
  // the default in once it arrives, without replacing anything already chosen.
  useEffect(() => {
    if (!open || editing || !defaultAccountId) return;
    setForm((f) => ({
      ...f,
      account_id: f.account_id || defaultAccountId,
      from_account_id: f.from_account_id || defaultAccountId,
    }));
  }, [open, editing, defaultAccountId]);

  const isTransfer = form.type === "transfer";
  const multipleAccounts = accounts.length > 1;
  // A transfer is created as one, or linked from two existing rows; an
  // ordinary transaction does not turn into one by switching the toggle.
  const types = TYPES.filter(({ value }) => {
    if (value !== "transfer") return !editing?.transfer_id;
    return multipleAccounts && (!editing || editing.transfer_id);
  });

  // Most-used first, so the categories a user reaches for are at the top.
  const catList = useMemo(
    () => sortCategoriesByUsage(
      form.type === "income" ? categories.income : categories.expense,
    ),
    [categories, form.type],
  );

  const setType = (t) => setForm((f) => ({ ...f, type: t, category: "" }));

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter an amount greater than 0");
    if (isTransfer && (!form.from_account_id || !form.to_account_id)) {
      return toast.error("Choose both accounts");
    }
    if (isTransfer && form.from_account_id === form.to_account_id) {
      return toast.error("Choose two different accounts");
    }
    if (!isTransfer && !form.category) return toast.error("Select a category");
    if (!form.date) return toast.error("Select a date");
    setSaving(true);
    const shared = {
      date: form.date,
      amount: Number(form.amount),
      description: form.description,
      recurring: form.recurring,
      recurrence_frequency: form.recurring ? form.recurrence_frequency : null,
    };
    try {
      if (isTransfer) {
        await onSaveTransfer(editing?.transfer_id || null, {
          ...shared,
          from_account_id: form.from_account_id,
          to_account_id: form.to_account_id,
        });
      } else {
        await onSave(editing, {
          ...shared,
          type: form.type,
          category: form.category,
          account_id: form.account_id || null,
        });
      }
      toast.success(editing ? "Transaction updated" : "Transaction added");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save transaction");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        className="bg-card border-border sm:max-w-md"
        data-testid="transaction-dialog"
        onOpenAutoFocus={focusAmount}
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="font-heading tracking-tight">
            {editing ? "Edit transaction" : "New transaction"}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="text-muted-foreground">
            Enter the amount, category and date for your transaction.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="space-y-4">
          {/* Type toggle */}
          {types.length > 1 && (
            <div
              className="grid gap-2 p-1 bg-secondary rounded-md"
              style={{ gridTemplateColumns: `repeat(${types.length}, minmax(0, 1fr))` }}
            >
              {types.map(({ value, label, active }) => (
                <button
                  key={value}
                  data-testid={`type-${value}-btn`}
                  onClick={() => setType(value)}
                  className={`py-2 rounded text-sm font-semibold transition-colors ${
                    form.type === value ? active : "text-muted-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Amount (€)</Label>
              <Input
                ref={amountRef}
                data-testid="txn-amount-input"
                type="number" min="0" step="0.01" inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
                className="mt-1 tabular-nums bg-background"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input
                data-testid="txn-date-input"
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="mt-1 bg-background"
              />
            </div>
          </div>

          {isTransfer ? (
            <div className="grid grid-cols-2 gap-3">
              <AccountSelect
                label="From" value={form.from_account_id} accounts={accounts}
                onChange={(v) => setForm((f) => ({ ...f, from_account_id: v }))}
                testid="txn-from-account-select"
              />
              <AccountSelect
                label="To" value={form.to_account_id} accounts={accounts}
                onChange={(v) => setForm((f) => ({ ...f, to_account_id: v }))}
                testid="txn-to-account-select"
              />
            </div>
          ) : multipleAccounts && (
            <AccountSelect
              label="Account" value={form.account_id} accounts={accounts}
              onChange={(v) => setForm((f) => ({ ...f, account_id: v }))}
              testid="txn-account-select"
            />
          )}

          {!isTransfer && (
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <CategoryPicker
                categories={catList}
                value={form.category}
                kind={form.type}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
              />
              {!catList.length && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  No {form.type} categories yet —{" "}
                  <Link to="/categories" className="text-income underline underline-offset-2">
                    create one
                  </Link>
                  .
                </p>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs text-muted-foreground">Description</Label>
            <Textarea
              data-testid="txn-description-input"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional note"
              className="mt-1 bg-background resize-none h-16"
            />
          </div>

          <div className="flex items-center justify-between border border-border rounded-md p-3">
            <div>
              <Label className="text-sm">Recurring</Label>
              <p className="text-xs text-muted-foreground">Repeats on a schedule</p>
            </div>
            <Switch
              data-testid="txn-recurring-switch"
              checked={form.recurring}
              onCheckedChange={(v) => setForm((f) => ({ ...f, recurring: v }))}
            />
          </div>

          {form.recurring && (
            <div>
              <Label className="text-xs text-muted-foreground">Frequency</Label>
              <Select value={form.recurrence_frequency} onValueChange={(v) => setForm((f) => ({ ...f, recurrence_frequency: v }))}>
                <SelectTrigger className="mt-1 bg-background" data-testid="txn-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {FREQS.map((fr) => (
                    <SelectItem key={fr} value={fr} className="capitalize">{fr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {editing && multipleAccounts && (
            <TransferLinkPanel
              transaction={editing}
              accountsById={accountsById}
              onFindCandidates={onFindTransferCandidates}
              onLink={onLinkTransfer}
              onUnlink={onUnlinkTransfer}
              onDone={() => onOpenChange(false)}
            />
          )}
        </div>

        <ResponsiveDialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="txn-cancel-btn">Cancel</Button>
          <Button
            data-testid="txn-save-btn"
            onClick={submit}
            disabled={saving}
            className="bg-income text-black hover:bg-income/90 font-semibold"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Add transaction"}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
